import type { WorkshopTopic } from '@/lib/api/types';
import { padIndex } from '@/lib/format';
import { WorkshopKit } from '@/features/viewer/components/WorkshopKit/WorkshopKit';
import styles from './TopicPanel.module.scss';

export type ViewMode = 'facilitator' | 'participant';

interface TopicPanelProps {
  topic: WorkshopTopic;
  mode: ViewMode;
  moduleIndex: number;
  topicIndex: number;
  topicCount: number;
  repoLink?: string | undefined;
}

/** Splits a plain-text note into paragraphs on blank lines. */
function paragraphs(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * One topic, rendered for the current audience.
 *
 * Participant mode renders *only* participant-facing fields. It is a display
 * split, not a security boundary: the server is what must withhold facilitator
 * notes from an attendee response (BR-AP6/NFR-AP1). Content is rendered as
 * text nodes rather than injected as HTML, so a manifest cannot smuggle markup
 * into the page.
 */
export function TopicPanel({
  topic,
  mode,
  moduleIndex,
  topicIndex,
  topicCount,
  repoLink,
}: TopicPanelProps) {
  const prompts = topic.participantPrompts ?? [];
  const [lead, ...rest] = prompts;

  return (
    <article className={styles.panel}>
      <p className={styles.meta}>
        <span>{topic.type}</span>
        <span aria-hidden="true">·</span>
        <span>
          Module {padIndex(moduleIndex)} · Topic {topicIndex + 1} of {topicCount}
        </span>
      </p>

      <h2 className={styles.title}>{topic.title}</h2>

      {mode === 'participant' ? (
        <>
          <p className={styles.summary}>{lead ?? topic.participantOutput}</p>
          {rest.length > 0 && (
            <ul className={styles.prompts}>
              {rest.map((prompt, index) => (
                <li key={index}>{prompt}</li>
              ))}
            </ul>
          )}
          <div className={styles.output}>
            <strong>Your output</strong>
            <span>{topic.participantOutput}</span>
          </div>
        </>
      ) : (
        <>
          <section className={styles.notes} aria-label="Facilitator notes">
            <p className={styles.notesLabel}>Facilitator notes</p>
            {paragraphs(topic.facilitatorNotes).map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </section>

          {prompts.length > 0 && (
            <section className={styles.participantBlock} aria-label="Participant content">
              <p className={styles.notesLabel}>Participant content</p>
              <ul className={styles.prompts}>
                {prompts.map((prompt, index) => (
                  <li key={index}>{prompt}</li>
                ))}
              </ul>
            </section>
          )}

          <div className={styles.output}>
            <strong>Participant output</strong>
            <span>{topic.participantOutput}</span>
          </div>

          <WorkshopKit repoLink={repoLink} />
        </>
      )}
    </article>
  );
}
